import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddPredictionForm } from '@modules/feedback/components/AddPredictions/AddPredictionForm';
import {
  useFetchReasonCandidatesForReason,
  useUpdateReason,
} from '@modules/taxonomy/hooks';
import { FormatQuoteRounded } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useState } from 'react';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { useGetUser } from 'v2/core/services/user';

interface AddReasonSuggestionProps {
  option: ReasonOrEntityLabelType;
  onCancel: () => void;
  onAfterAddingReasonSuggestion: (
    prediction: ReasonOrEntityLabelType,
    hasAddedReasonCandidate: boolean,
    addedReasonCandidates?: string[],
  ) => void;
  relatedRCs?: string[];
}

const MAX_RC_TO_SHOW = 5;

export const AddReasonSuggestion = ({
  option,
  onCancel,
  onAfterAddingReasonSuggestion,
  relatedRCs,
}: AddReasonSuggestionProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const haveRelatedRCs = relatedRCs && relatedRCs?.length > 0;
  const { loading, reasonCandidates: relatedRCForReason } =
    useFetchReasonCandidatesForReason(option.id, haveRelatedRCs);

  const reasonCandidates = haveRelatedRCs ? relatedRCs : relatedRCForReason;

  const [
    shouldAddReasonCandidateToCurrentReason,
    setShouldAddReasonCandidateToCurrentReason,
  ] = useState(false);

  const { updateReason } = useUpdateReason({
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to add reason suggestion.',
        props: {},
      });
    },
  });

  const onSubmit = (event: FormEvent | KeyboardEvent | undefined) => {
    event?.preventDefault();
    if (shouldAddReasonCandidateToCurrentReason) {
      updateReason({
        variables: {
          userID,
          tenantID,
          id: option.id,
          addReasonCandidates: reasonCandidates,
        },
      });
    }

    onAfterAddingReasonSuggestion(
      option,
      shouldAddReasonCandidateToCurrentReason,
      reasonCandidates,
    );
  };

  const reasonCandidatesNode = () => {
    const totalReasonCandidatesCount = reasonCandidates?.length ?? 0;
    const reasonCandidatesToDisplay = reasonCandidates?.slice(
      0,
      MAX_RC_TO_SHOW,
    );

    if (loading) {
      return <Skeleton variant="text" width="100%" height="5" />;
    }
    return (
      <>
        "{reasonCandidatesToDisplay?.join(`', '`)}"
        {totalReasonCandidatesCount > MAX_RC_TO_SHOW && (
          <Tooltip
            title={`"${reasonCandidates
              ?.slice(MAX_RC_TO_SHOW, totalReasonCandidatesCount)
              .join(`', '`)}"`}
          >
            <Box component="span">
              + {totalReasonCandidatesCount - MAX_RC_TO_SHOW} more
            </Box>
          </Tooltip>
        )}
      </>
    );
  };

  const labelNode = (
    <Box
      sx={{
        paddingLeft: 1,
      }}
      component="p"
    >
      <Typography component="span" color="text.secondary">
        Predict{' '}
        <Typography component="span" color="text.primary">
          <FormatQuoteRounded
            color="primary"
            sx={{
              fontSize: '28px',
            }}
          />
          {option.displayName}
        </Typography>{' '}
        in all future records where the user talks about
        <Typography component="span" color="text.primary">
          {' '}
          {reasonCandidatesNode()}
        </Typography>
      </Typography>
    </Box>
  );

  return (
    <AddPredictionForm
      onCancel={onCancel}
      onSubmit={onSubmit}
      displayName={option.displayName}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={shouldAddReasonCandidateToCurrentReason}
            onChange={(event) =>
              setShouldAddReasonCandidateToCurrentReason(event.target.checked)
            }
          />
        }
        sx={{
          alignItems: 'flex-start',
        }}
        label={labelNode}
      />
    </AddPredictionForm>
  );
};
